// apps/client/src/client/services/role/role.ts

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axios } from "@/client/libs/axios";
import type {
  RoleDto,
  CreateRoleDto,
  UpdateRoleDto,
  AssignPermissionsDto,
} from "@reactive-resume/dto";

export type Role = RoleDto;

export const fetchRoles = async (): Promise<Role[]> => {
  const response = await axios.get<Role[]>("/role");
  return response.data;
};

export const fetchRole = async (id: string): Promise<Role> => {
  const response = await axios.get<Role>(`/role/${id}`);
  return response.data;
};

export const createRole = async (data: CreateRoleDto): Promise<Role> => {
  const response = await axios.post<Role>("/role", data);
  return response.data;
};

export const updateRole = async (id: string, data: UpdateRoleDto): Promise<Role> => {
  const response = await axios.patch<Role>(`/role/${id}`, data);
  return response.data;
};

export const deleteRole = async (id: string): Promise<void> => {
  await axios.delete(`/role/${id}`);
};

export const assignPermissionsToRole = async (
  roleId: string,
  data: AssignPermissionsDto,
): Promise<Role> => {
  const response = await axios.post<Role>(`/role/${roleId}/permissions`, data);
  return response.data;
};

export const addPermissionToRole = async (roleId: string, permissionId: string): Promise<Role> => {
  const response = await axios.post<Role>(`/role/${roleId}/permissions/${permissionId}`);
  return response.data;
};

export const removePermissionFromRole = async (
  roleId: string,
  permissionId: string,
): Promise<Role> => {
  const response = await axios.delete<Role>(`/role/${roleId}/permissions/${permissionId}`);
  return response.data;
};

// export const assignRoleToUser = async (userId: string, roleId: string): Promise<void> => {
//   await axios.post(`/role/${roleId}/users/${userId}`);
// };

// export const removeRoleFromUser = async (userId: string, roleId: string): Promise<void> => {
//   await axios.delete(`/role/${roleId}/users/${userId}`);
// };

// Hooks
export const useRoles = () => {
  return useQuery({
    queryKey: ["roles"],
    queryFn: fetchRoles,
  });
};

export const useRole = (id: string) => {
  return useQuery({
    queryKey: ["role", id],
    queryFn: () => fetchRole(id),
    enabled: !!id,
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleDto }) => updateRole(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role", id] });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};

export const useAssignPermissions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, data }: { roleId: string; data: AssignPermissionsDto }) =>
      assignPermissionsToRole(roleId, data),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role", roleId] });
    },
  });
};

export const useAddPermissionToRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, permissionId }: { roleId: string; permissionId: string }) =>
      addPermissionToRole(roleId, permissionId),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role", roleId] });
    },
  });
};

export const useRemovePermissionFromRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, permissionId }: { roleId: string; permissionId: string }) =>
      removePermissionFromRole(roleId, permissionId),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role", roleId] });
    },
  });
};

// apps/client/src/client/services/role/role.ts (ajouter)

export const assignRoleToUser = async (userId: string, roleId: string): Promise<void> => {
  await axios.post(`/role/${roleId}/users/${userId}`);
};

export const removeRoleFromUser = async (userId: string, roleId: string): Promise<void> => {
  await axios.delete(`/role/${roleId}/users/${userId}`);
};

export const useAssignRoleToUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      assignRoleToUser(userId, roleId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["filter-admin"] });
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
};

export const useRemoveRoleFromUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      removeRoleFromUser(userId, roleId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["filter-admin"] });
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
};
